/**
 * DateRangeSelect — compact dropdown built on the existing SelectV2 wrapper
 * (react-select v3). Generic over a string-valued option set so the same
 * primitive can drive the home-dashboard range dropdown today and any
 * other "pick one of N tonal options" header control later.
 *
 * Search + clear are disabled because the option set is always tiny and
 * the dropdown sits in a tight header. menuPortalTarget pushes the
 * popover to <body> via SelectV2's existing portal so the dropdown
 * escapes any overflow-hidden parent.
 */

import * as React from "react";
import SelectV2 from "components/react-selectv2";

export interface DateRangeSelectOption<T extends string = string> {
  label: string;
  value: T;
}

export interface DateRangeSelectProps<T extends string = string> {
  options: ReadonlyArray<DateRangeSelectOption<T>>;
  value: T;
  onChange: (next: T) => void;
  /** Container width — defaults to 140px which fits all 4 dashboard labels. */
  width?: number | string;
  className?: string;
  /** Accessible label for screen readers. */
  ariaLabel?: string;
}

export function DateRangeSelect<T extends string = string>({
  options,
  value,
  onChange,
  width = 140,
  className = "",
  ariaLabel,
}: DateRangeSelectProps<T>) {
  const selected =
    options.find((o) => o.value === value) ?? options[0];

  return (
    <div className={className} style={{ minWidth: width, width }}>
      <SelectV2
        options={options as unknown as DateRangeSelectOption<string>[]}
        value={selected as unknown as DateRangeSelectOption<string>}
        isSearchable={false}
        isClearable={false}
        aria-label={ariaLabel}
        onChange={(next) => {
          if (
            next &&
            typeof next === "object" &&
            "value" in (next as Record<string, unknown>)
          ) {
            onChange((next as DateRangeSelectOption<T>).value);
          }
        }}
      />
    </div>
  );
}
